import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { AlertLevelsService, AlertStatusesService, TagsService, DevicesService, AssetsService } from '../../services';
import { Popover, OverlayTrigger, ButtonToolbar } from 'react-bootstrap';
import i18next from "i18next";
import config from "config";
import { MultiRowSelection } from '../MultiRowSelection';

const containsId = (idSetToTest, id) => {
  if (idSetToTest) {
    for (let i = 0; i < idSetToTest.length; i++) {
      if (idSetToTest[i] == id)
        return true
    }
  }
  return false
};

const LevelPopover = forwardRef(({ filterByLevelIds, onUpdate, onError, ...props }, ref) => {
  const [alertLevels, setAlertLevels] = useState(null);
  const [displayOptions, setDisplayOptions] = useState(null);

  useEffect(() => {
    AlertLevelsService.listAll().then((response) => {
      setAlertLevels(response.alertLevels);
    }).catch((error) => {
      onError(i18next.t('messages.could-not-load-alert-levels'));
      console.error(error);
    });
  }, [onError]);

  useEffect(() => {
    if (alertLevels) {
      setDisplayOptions(alertLevels.map(({ id, token }) => ({
        id: id,
        icon: null,
        className: '',
        name: i18next.t(`alert-levels.${token}`),
        selected: filterByLevelIds == null || containsId(filterByLevelIds, id)
      })));
    }
  }, [alertLevels, filterByLevelIds]);

  const updateOptions = useCallback((options) => {
    onUpdate(options.filter((option) => option.selected).map((option) => option.id));
    setDisplayOptions(options);
  }, [onUpdate]);

  return (
    <Popover ref={ref} {...props} className="filter-map-cat-selection filter-map-popover" id="level-selection-popover" title={ i18next.t('pages.alerts.level') }>
      { 
        displayOptions ? <MultiRowSelection options={displayOptions} onApplyFilters={updateOptions} /> : <div className="loader">{ i18next.t('loading') }</div>
      }
    </Popover>
  );
});

const StatusPopover = forwardRef(({ filterByStatusIds, onUpdate, onError, ...props }, ref) => {
  const [alertStatuses, setAlertStatuses] = useState(null);
  const [displayOptions, setDisplayOptions] = useState(null);

  useEffect(() => {
    AlertStatusesService.listAll().then((response) => {
      setAlertStatuses(response.alertStatuses);
    }).catch((error) => {
      onError(i18next.t('messages.could-not-load-device'));
      console.error(error);
    });
  }, [onError]);

  useEffect(() => {
    if (alertStatuses) {
      setDisplayOptions(alertStatuses.map(({ id, token }) => ({
        id: id,
        icon: null,
        className: '',
        name: i18next.t(`alert-statuses.${token}`),
        selected: filterByStatusIds == null || containsId(filterByStatusIds, id)
      })));
    }
  }, [alertStatuses, filterByStatusIds]);

  const updateOptions = useCallback((options) => {
    onUpdate(options.filter((option) => option.selected).map((option) => option.id));
    setDisplayOptions(options);
  }, [onUpdate]);

  return (
    <Popover ref={ref} {...props} className="filter-map-cat-selection filter-map-popover" id="status-selection-popover" title={ i18next.t('pages.alerts.status') }>
      {
        displayOptions ? <MultiRowSelection options={displayOptions} onApplyFilters={updateOptions} /> : <div className="loader">{ i18next.t('loading') }</div>
      }
    </Popover>
  );
});

const EntityPopover = forwardRef(({ deviceGroupingId, filterByDeviceIds, filterByAssetIds, onUpdate, onError, ...props }, ref) => {
  const [assets, setAssets] = useState(null);
  const [devices, setDevices] = useState(null);
  const [displayOptions, setDisplayOptions] = useState(null);

  useEffect(() => {
    DevicesService.listAllForGrouping(deviceGroupingId, false, false, true)
      .then((response) => {
        setDevices(response.devices)
      })
      .catch((error) => {        
        console.error(error);
        onError(i18next.t("messages.could-not-load-devices"));
      });

    AssetsService.listAllForGrouping(deviceGroupingId)
      .then((response) => {
        setAssets(response.assets);
      })
      .catch((error) => {        
        console.error(error);
        onError(i18next.t("messages.could-not-load-devices"));
      });
  }, [onError]);

  useEffect(() => {
    if (assets && devices) {
      const combined = devices.map(({ id, displayName }) => ({
          id: id, icon: null, className: '', name: displayName, selected: filterByDeviceIds == null || containsId(filterByDeviceIds, id), entityType: 'device'
        })).concat(
        assets.map(({ id, name }) => ({
          id: id, icon: null, className: '', name: name, selected: filterByAssetIds == null || containsId(filterByAssetIds, id), entityType: 'asset'
        }))
      ).sort((entityA, entityB) =>  entityA.name.localeCompare(entityB.name));

      setDisplayOptions(combined);
    }
  }, [assets, devices, filterByDeviceIds, filterByAssetIds]);

  const onApplyFilters = useCallback((options) => {
    onUpdate(
      options.filter((entity) => entity.entityType === 'device').filter((device) => device.selected).map((device) => device.id),
      options.filter((entity) => entity.entityType === 'asset').filter((asset) => asset.selected).map((asset) => asset.id)
    );
    setDisplayOptions(options);
  }, [onUpdate]);

  return (
    <Popover ref={ref} {...props} className="filter-map-cat-selection filter-map-popover" id="entity-selection-popover" title={ i18next.t('pages.alerts.asset') }>
      {
        displayOptions ? <MultiRowSelection options={displayOptions} onApplyFilters={onApplyFilters} /> : <div className="loader">{ i18next.t('loading') }</div>
      }
    </Popover>
  );
});

const AreaPopover = forwardRef(({ filterByAreaTagIds, onUpdate, onError, ...props }, ref) => {
  const [allAreas, setAllAreas] = useState(null);
  const [displayOptions, setDisplayOptions] = useState(null);

  useEffect(() => {
    TagsService.listAll().then((response) => {
        setAllAreas(response.tags.filter((tag) => tag.tagType && tag.tagType.token === "area"));
      }).catch((error) => {        
        console.error(error);
        onError(i18next.t("messages.could-not-load-areas"));
      });
  }, [onError]);

  useEffect(() => {
    if (allAreas) {
      setDisplayOptions(
        allAreas
          .map(({ id, name }) => ({
              id: id, icon: null, className: "", name: name, selected: filterByAreaTagIds == null || containsId(filterByAreaTagIds, id), 
          }))
      );
    }
  }, [allAreas, filterByAreaTagIds]);

  const areaFilterUpdated = useCallback((options) => {
    onUpdate(options.filter((area) => area.selected).map((area) => area.id));
    setDisplayOptions(options);
  }, [onUpdate])

  return (
    <Popover ref={ref} {...props} className="filter-map-cat-selection filter-map-popover" id="area-selection-popover" title={ i18next.t('pages.alerts.area') }>
      {
        displayOptions ? <MultiRowSelection options={displayOptions} onApplyFilters={areaFilterUpdated} />  : <div className="loader">{ i18next.t('loading') }</div>
      }
    </Popover>
  );
});

const NotesPopover = forwardRef(({ notesInclude, onUpdate, ...props }, ref) => {
  const [localIncludeNotesFilter, setLocalIncludeNotesFilter] = useState(notesInclude ?? { with: true, without: true });

  const applyUpdate = useCallback(() => {
    onUpdate(localIncludeNotesFilter);
  }, [localIncludeNotesFilter, onUpdate]);  

  return (
    <Popover ref={ref} {...props} className="filter-map-cat-selection filter-map-popover" id="notes-selection-popover" title={ i18next.t('pages.alerts.notes') }>
      <button className="btn btn-sm btn-default modal-filter-button" onClick={applyUpdate}> { i18next.t('apply-filters') } </button>
      <ul>
        <li onClick={() => setLocalIncludeNotesFilter({...localIncludeNotesFilter, with: !localIncludeNotesFilter.with})}>
          { i18next.t('pages.alerts.with-notes') }
          <img style={{ display: localIncludeNotesFilter.with ? 'inline' : 'none' }} width='18' height='18' src={`${config.WEB_BASE_URL}/images/icon/check_icon.svg`} />
        </li>
        <li onClick={() => setLocalIncludeNotesFilter({...localIncludeNotesFilter, without: !localIncludeNotesFilter.without})}>
          { i18next.t('pages.alerts.without-notes') }
          <img style={{ display: localIncludeNotesFilter.without ? 'inline' : 'none' }} width='18' height='18' src={`${config.WEB_BASE_URL}/images/icon/check_icon.svg`} />
        </li>
      </ul>
    </Popover>
  );
});

const alertListFilterToOptions = (filter) => {
  let options = [];

  if (filter.filterByLevelIds) {
    options.push({ 'filterByLevelIds': filter.filterByLevelIds.join(",") });
  }

  if (filter.filterByStatusIds) {
    options.push({ 'filterByStatusIds': filter.filterByStatusIds.join(",") });
  }

  if (filter.filterByAreaTagIds) {
    options.push({ 'filterByAreaTagIds': filter.filterByAreaTagIds.join(",") });
  }

  if (filter.filterByDeviceIds) {
    options.push({ 'filterByDeviceIds': filter.filterByDeviceIds.join(",") });
  }
  
  if (filter.filterByAssetIds) {
    options.push({ 'filterByAssetIds': filter.filterByAssetIds.join(",") });
  }

  if (filter.notesInclude) {
    options.push({ 'includeWithNotes': filter.notesInclude.with});
    options.push({ 'includeWithoutNotes': filter.notesInclude.without});
  }

  return options;
};

const alertListFilterExtractFromState = ({ filter }) => {
  return {
    filterByLevelIds: filter?.filterByLevelIds,
    filterByStatusIds: filter?.filterByStatusIds,
    filterByAreaTagIds: filter?.filterByAreaTagIds,
    filterByDeviceIds: filter?.filterByDeviceIds,
    filterByAssetIds: filter?.filterByAssetIds,
    notesInclude: filter?.notesInclude
  };
};

const alertListFilterExtractFromParams = (inputFilter, parsedParams) => {
  let filter = { ...inputFilter };

  if (!filter.notesInclude) {
    filter['notesInclude'] = {
      with: parsedParams['includeWithNotes'] == null ? true : parsedParams['includeWithNotes'],
      without: parsedParams['includeWithoutNotes'] == null ? true : parsedParams['includeWithoutNotes']
    };
  }

  if (!filter.filterByLevelIds) {
    filter["filterByLevelIds"] = parsedParams["filterByLevelIds"] == null ? null : parsedParams["filterByLevelIds"].split(",").map((id) => parseInt(id));
  }

  if (!filter.filterByStatusIds) {
    filter['filterByStatusIds'] = parsedParams["filterByStatusIds"] == null ? null : parsedParams["filterByStatusIds"].split(",").map((id) => parseInt(id));
  }

  if (!filter.filterByAreaTagIds) {
    filter['filterByAreaTagIds'] = parsedParams['filterByAreaTagIds'] == null ? null : parsedParams['filterByAreaTagIds'].split(',').map(id => parseInt(id));
  }

  if (!filter.filterByAssetIds) {
    filter['filterByAssetIds'] = parsedParams['filterByAssetIds'] == null || parsedParams['filterByAssetIds'] == '' ? null : parsedParams['filterByAssetIds'].split(",").map((id) => parseInt(id));
  }

  if (!filter.filterByDeviceIds) {
    filter['filterByDeviceIds'] = parsedParams['filterByDeviceIds'] == null ? null : parsedParams["filterByDeviceIds"].split(",").map((id) => parseInt(id));
  }

  return filter;
};

const AlertListFilterArea = ({ filter, deviceGroupingId, onFilterChange, onError }) => {
  const levelUpdated = useCallback((filterByLevelIds) => {
    onFilterChange({ ...filter, filterByLevelIds: filterByLevelIds });
  }, [filter, onFilterChange]);

  const statusUpdated = useCallback((filterByStatusIds) => {
    onFilterChange({ ...filter, filterByStatusIds: filterByStatusIds });
  }, [filter, onFilterChange]);

  const entityFilterUpdated = useCallback((filterByDeviceIds, filterByAssetIds) => {
    onFilterChange({ ...filter, filterByDeviceIds: filterByDeviceIds, filterByAssetIds: filterByAssetIds });
  }, [filter, onFilterChange]);

  const areaFilterUpdated = useCallback((filterByAreaTagIds) => {
    onFilterChange({ ...filter, filterByAreaTagIds: filterByAreaTagIds });
  }, [filter, onFilterChange]);

  const notesFlagsUpdated = useCallback((notesInclude) => {
    onFilterChange({ ...filter, notesInclude: notesInclude });
  }, [filter, onFilterChange]);

  return (
    <div className="map-filter-wrapper">
      <ButtonToolbar>
        <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={ 
          <LevelPopover filterByLevelIds={filter.filterByLevelIds} onUpdate={levelUpdated} onError={onError} /> 
        }>
          <a className="map-filter-button level-filter"><img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/filter-level.svg`} alt={ i18next.t('pages.alerts.level') }/>{ i18next.t('pages.alerts.level') }</a>
        </OverlayTrigger>

        <OverlayTrigger  trigger="click" rootClose placement="bottom" overlay={ 
          <StatusPopover filterByStatusIds={filter.filterByStatusIds} onUpdate={statusUpdated} onError={onError} /> 
        }>
          <a className="map-filter-button status-filter"><img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/filter-status.svg`} alt={ i18next.t('pages.alerts.status') }/>{ i18next.t('pages.alerts.status') }</a>
        </OverlayTrigger>

        <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={ 
          <EntityPopover deviceGroupingId={deviceGroupingId} filterByDeviceIds={filter.filterByDeviceIds} filterByAssetIds={filter.filterByAssetIds} onUpdate={entityFilterUpdated} onError={onError} /> 
        }>
          <a className="map-filter-button status-filter"><img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/filter-device.svg`} alt={ i18next.t('pages.alerts.asset') }/>{ i18next.t('pages.alerts.asset') }</a>
        </OverlayTrigger>

        <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={ 
          <AreaPopover filterByAreaTagIds={filter.filterByAreaTagIds} onUpdate={areaFilterUpdated} onError={onError} /> 
        }>
          <a className="map-filter-button area-filter"><img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/filter-area.svg`} alt={ i18next.t('pages.alerts.area') } />{ i18next.t('pages.alerts.area') }</a>
        </OverlayTrigger>

        <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={ 
          <NotesPopover notesInclude={filter.notesInclude} onUpdate={notesFlagsUpdated} />
        }>
          <a className="map-filter-button level-filter"><img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/filter-notes.svg`} alt={ i18next.t('pages.alerts.notes') } />{ i18next.t('pages.alerts.notes') }</a>
        </OverlayTrigger>
      </ButtonToolbar>
    </div>
  );
};

export {
  alertListFilterToOptions,
  alertListFilterExtractFromState,
  alertListFilterExtractFromParams,
  AlertListFilterArea
};
